import { useCallback, useEffect, useState } from 'react';

import { PagedResponse, usePrevious } from '@feature/utils';

import { PaginationProps } from './Pagination';

const DEFAULT_SIZE = 12;

interface UsePaginationReturn {
  pagination: { page: number; pageSize: number };
  useTotalElements: (response: PagedResponse<unknown> | undefined | number) => void;
  resetPagination: () => void;
}

interface UsePagingOptions {
  pageSize?: number;
  search?: string;
}

export function usePagination(options?: UsePagingOptions): UsePaginationReturn & PaginationProps {
  const [currentPage, setCurrentPage] = useState(0);
  const [totalElements, setTotalElements] = useState<number | undefined>();
  const currentSize = options?.pageSize ?? DEFAULT_SIZE;
  const currentSearch = options?.search ?? '';

  const prevCurrentSize = usePrevious(currentSize);

  useEffect(() => {
    if (prevCurrentSize != currentSize) {
      setCurrentPage(0);
    }
  }, [prevCurrentSize, currentSize]);

  const prevSearch = usePrevious(currentSearch);
  useEffect(() => {
    if (prevSearch != currentSearch) {
      setCurrentPage(0);
    }
  }, [prevSearch, currentSearch]);

  function useTotalElements(response: PagedResponse<unknown> | undefined | number) {
    useEffect(() => {
      if (response == null) return;

      if (!isNaN(response as number)) {
        setTotalElements(response as number);
      } else if ((response as PagedResponse<unknown> | undefined)?.count != null) {
        setTotalElements((response as PagedResponse<unknown> | undefined)?.count);
      }
      // TODO fix eslint
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [response, (response as PagedResponse<unknown> | undefined)?.count]);
  }

  const resetPagination = useCallback(() => {
    setCurrentPage(0);
  }, []);

  return {
    pagination: { page: currentPage, pageSize: currentSize },
    currentPage,
    useTotalElements,
    totalPages: totalElements ? Math.ceil(totalElements / currentSize) : null,
    onChange: setCurrentPage,
    pageSize: currentSize,
    totalElements,
    resetPagination,
  };
}
